import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {
	Task,
	getTaskCustomer,
	getTaskCustomerInitials,
	getTaskPeriodShortLabel,
	getTaskPrice,
	getTaskServicesCount,
	getTaskStatusBadgeAlign,
	getTaskStatusLabel,
	getTaskTitle,
} from 'entities/task';
import {
	OrderActionDrawers,
	RECEIPT_ERROR_LABEL,
	isFreeSmzReceiptError,
	useOrderActions,
} from 'features/order-actions';
import {ActionMenu, Button} from 'ui';
import {ExclamationMark} from 'shared/ui/ExclamationMark';
import {lkPublicImg} from 'shared/lib/publicAssets';
import OrderCardArrowIcon from 'shared/assets/svg/order-card-arrow.svg?react';
import OrderCardBriefcaseIcon from 'shared/assets/svg/order-card-briefcase.svg?react';
import OrderCardUserPlaceholderIcon from 'shared/assets/svg/order-card-user-placeholder.svg?react';
import styles from './OrdersTable.module.css';

function AlertChevronIcon(): React.ReactElement {
	return (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M11 18V6L20 12L11 18Z" fill="black" />
			<path d="M11 11.1992H4V12.7992H11V11.1992Z" fill="black" />
		</svg>
	);
}

function isAlertStyleCard(task: Task): boolean {
	return Boolean(task.errorText?.trim()) && (task.status === 'error' || task.status === 'canceled');
}

function shouldShowStatusBadge(task: Task): boolean {
	if (isFreeSmzReceiptError(task)) return false;
	if (isAlertStyleCard(task)) return false;
	if (task.status === 'error') return false;
	if (!task.status) return false;

	return getTaskStatusLabel(task.status) !== '—';
}

function OrderMobileActions({
	task,
	orderActions,
}: {
	task: Task;
	orderActions: ReturnType<typeof useOrderActions>;
}): React.ReactElement | null {
	const {actions, signAcceptance, createReceipt, declineDrawer, employmentMismatch} = orderActions;
	const drawers = (
		<OrderActionDrawers
			task={task}
			signAcceptance={signAcceptance}
			createReceipt={createReceipt}
			declineDrawer={declineDrawer}
			employmentMismatch={employmentMismatch}
		/>
	);

	if (actions.length === 0) return null;

	if (actions.length === 1) {
		const action = actions[0]!;

		return (
			<div className={styles.cardFooter}>
				<Button
					type="button"
					fullWidth
					className={styles.cardActionButton}
					onClick={action.onSelect}
					disabled={action.disabled}
				>
					<span className={styles.cardActionLabel}>{action.label}</span>
					<OrderCardArrowIcon />
				</Button>
				{drawers}
			</div>
		);
	}

	return (
		<div className={`${styles.cardFooter} ${styles.cardFooterAlignEnd}`}>
			<ActionMenu
				items={actions.map((a) => ({
					key: a.id,
					label: a.label,
					onSelect: a.onSelect,
					disabled: a.disabled,
				}))}
				triggerLabel="Действия"
			/>
			{drawers}
		</div>
	);
}

export function OrdersMobileCard({task}: {task: Task}): React.ReactElement {
	const [cardBackgroundIndex] = useState(() => Math.floor(Math.random() * 20) + 1);
	const cardBackgroundUrl = lkPublicImg.orderCardBackground(cardBackgroundIndex);

	const orderActions = useOrderActions(task, 'table');
	const hasReceiptError = isFreeSmzReceiptError(task);
	const orderId = task.id;
	const title = getTaskTitle(task.title, task.type, task.id);
	const customer = getTaskCustomer(task);
	const price = getTaskPrice(task);
	const period = getTaskPeriodShortLabel(task);
	const count = getTaskServicesCount(task);
	const initials = getTaskCustomerInitials(customer);
	const showPaymentErrorBanner = isAlertStyleCard(task);
	const showErrorBanner = showPaymentErrorBanner || hasReceiptError;

	const main = (
		<>
			<div className={styles.cardCustomerRow}>
				<div className={styles.cardAvatar} aria-hidden="true">
					{initials ? (
						<span className={styles.cardAvatarLetters}>{initials}</span>
					) : (
						<OrderCardUserPlaceholderIcon aria-hidden />
					)}
				</div>
				<span className={styles.cardCustomerName}>{customer}</span>
			</div>
			<div className={styles.cardTitle}>{title}</div>
			<div className={styles.cardMetaRow}>
				<span className={styles.cardMetaPeriodPrice}>
					{[period !== '—' ? period : null, price !== '—' ? price : null].filter(Boolean).join(' • ')}
				</span>
				{count > 0 ? (
					<span className={styles.cardMetaCount}>
						<OrderCardBriefcaseIcon className={styles.metaIcon} aria-hidden />
						{count}
					</span>
				) : null}
			</div>
		</>
	);

	const showBadge = shouldShowStatusBadge(task);
	const align = getTaskStatusBadgeAlign(task.status);
	const statusLabel = getTaskStatusLabel(task.status);

	const errorBanner = hasReceiptError ? (
		orderId != null ? (
			<Link
				to={`/orders/${orderId}`}
				className={styles.errorBanner}
				aria-label={`Открыть заказ: ${RECEIPT_ERROR_LABEL}`}
			>
				<ExclamationMark rounded className={styles.alertIcon} />
				<span className={styles.alertText}>{RECEIPT_ERROR_LABEL}</span>
				<span className={styles.alertChevron} aria-hidden="true">
					<AlertChevronIcon />
				</span>
			</Link>
		) : (
			<div className={styles.errorBanner} role="alert">
				<ExclamationMark rounded className={styles.alertIcon} />
				<span className={styles.alertText}>{RECEIPT_ERROR_LABEL}</span>
			</div>
		)
	) : showPaymentErrorBanner ? (
		orderId != null ? (
			<Link
				to={`/orders/${orderId}`}
				className={styles.errorBanner}
				aria-label={`Открыть заказ: ${task.errorText}`}
			>
				<ExclamationMark rounded className={styles.alertIcon} />
				<span className={styles.alertText}>{task.errorText}</span>
				<span className={styles.alertChevron} aria-hidden="true">
					<AlertChevronIcon />
				</span>
			</Link>
		) : (
			<div className={styles.errorBanner} role="alert">
				<ExclamationMark rounded className={styles.alertIcon} />
				<span className={styles.alertText}>{task.errorText}</span>
				<span className={styles.alertChevron} aria-hidden="true">
					<AlertChevronIcon />
				</span>
			</div>
		)
	) : null;

	const cardClassName = [
		styles.mobileCard,
		showErrorBanner ? styles.mobileCardWithError : showBadge ? styles.mobileCardWithTopBadge : null,
	]
		.filter(Boolean)
		.join(' ');

	return (
		<article className={cardClassName}>
			{showBadge ? (
				<div className={styles.statusBadgeWrap} data-align={align}>
					<span className={styles.statusBadge}>{statusLabel}</span>
				</div>
			) : null}
			{errorBanner}
			<div className={styles.cardBody}>
				<div className={styles.cardBodyPattern} style={{backgroundImage: `url(${cardBackgroundUrl})`}} aria-hidden />
				{orderId != null ? (
					<Link to={`/orders/${orderId}`} className={styles.cardBodyLink} aria-label={`Открыть заказ: ${title}`}>
						<div className={styles.cardBodyPad}>{main}</div>
					</Link>
				) : (
					<div className={styles.cardBodyPad}>{main}</div>
				)}
				<OrderMobileActions task={task} orderActions={orderActions} />
			</div>
		</article>
	);
}
